import { useState, useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

interface LocationData {
  city: string;
  address: string;
  latitude: number | null;
  longitude: number | null;
}

interface LocationAutocompleteProps {
  value: string;
  latitude: number | null;
  longitude: number | null;
  onChange: (data: LocationData) => void;
  placeholder?: string;
  label?: string;
  className?: string;
  /** Radius in km to show on the map (0 = no circle) */
  radius?: number;
  /** Whether to show the map below the input (default true when coordinates exist) */
  showMap?: boolean;
}

interface NominatimResult {
  display_name: string;
  lat: string;
  lon: string;
  address: {
    road?: string;
    house_number?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    county?: string;
    state?: string;
    country?: string;
    suburb?: string;
    neighbourhood?: string;
  };
}

// Fix default Leaflet marker icon paths (broken by bundlers)
const defaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

function extractCity(addr: NominatimResult["address"]): string {
  return addr.city || addr.town || addr.village || addr.municipality || "";
}

function extractStreet(addr: NominatimResult["address"]): string {
  const parts: string[] = [];
  if (addr.road) parts.push(addr.road);
  if (addr.house_number) parts.push(addr.house_number);
  return parts.join(" ");
}

async function reverseGeocode(
  lat: number,
  lng: number,
): Promise<{ city: string; address: string; street: string } | null> {
  try {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lon: lng.toString(),
      format: "json",
      addressdetails: "1",
      "accept-language": "lt",
    });
    const resp = await fetch(
      `https://nominatim.openstreetmap.org/reverse?${params}`,
      { headers: { "User-Agent": "Boardo/1.0" } },
    );
    if (!resp.ok) return null;
    const data: NominatimResult = await resp.json();
    const city = extractCity(data.address);
    const street = extractStreet(data.address);
    return { city, address: data.display_name, street };
  } catch {
    return null;
  }
}

export function LocationAutocomplete({
  value,
  latitude,
  longitude,
  onChange,
  placeholder = "pvz. Vilnius",
  label,
  className = "",
  radius = 0,
  showMap = true,
}: LocationAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value || "");
  const [fullAddress, setFullAddress] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isLocating, setIsLocating] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Map refs
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const circleRef = useRef<L.Circle | null>(null);

  const hasCoordinates = latitude != null && longitude != null;

  // Sync external value changes
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Cleanup map on unmount
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
        circleRef.current = null;
      }
    };
  }, []);

  // Initialize / update Leaflet map
  useEffect(() => {
    if (!showMap || !hasCoordinates || !mapContainerRef.current) return;

    const lat = latitude!;
    const lng = longitude!;

    if (!mapRef.current) {
      const map = L.map(mapContainerRef.current, {
        center: [lat, lng],
        zoom: 13,
        scrollWheelZoom: true,
      });
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>',
        maxZoom: 19,
      }).addTo(map);

      // Draggable marker
      const marker = L.marker([lat, lng], {
        icon: defaultIcon,
        draggable: true,
      }).addTo(map);

      marker.on("dragend", async () => {
        const pos = marker.getLatLng();
        const result = await reverseGeocode(pos.lat, pos.lng);
        if (result) {
          const display =
            result.city ||
            result.address.split(",").slice(0, 2).join(",").trim();
          setInputValue(display);
          setFullAddress(result.address);
          setStreetAddress(result.street);
          onChange({
            city: result.city,
            address: result.address,
            latitude: pos.lat,
            longitude: pos.lng,
          });
        } else {
          onChange({
            city: "",
            address: "",
            latitude: pos.lat,
            longitude: pos.lng,
          });
        }
        if (circleRef.current) {
          circleRef.current.setLatLng(pos);
        }
      });

      markerRef.current = marker;
      mapRef.current = map;

      if (radius > 0) {
        const circle = L.circle([lat, lng], {
          radius: radius * 1000,
          color: "#6366f1",
          fillColor: "#6366f1",
          fillOpacity: 0.1,
          weight: 2,
        }).addTo(map);
        circleRef.current = circle;
        map.fitBounds(circle.getBounds(), { padding: [20, 20] });
      }
    } else {
      const map = mapRef.current;
      map.setView([lat, lng], map.getZoom());

      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lng]);
      }

      if (circleRef.current) {
        if (radius > 0) {
          circleRef.current.setLatLng([lat, lng]);
          circleRef.current.setRadius(radius * 1000);
          map.fitBounds(circleRef.current.getBounds(), { padding: [20, 20] });
        } else {
          circleRef.current.remove();
          circleRef.current = null;
        }
      } else if (radius > 0 && map) {
        const circle = L.circle([lat, lng], {
          radius: radius * 1000,
          color: "#6366f1",
          fillColor: "#6366f1",
          fillOpacity: 0.1,
          weight: 2,
        }).addTo(map);
        circleRef.current = circle;
        map.fitBounds(circle.getBounds(), { padding: [20, 20] });
      }
    }

    setTimeout(() => mapRef.current?.invalidateSize(), 100);
  }, [latitude, longitude, radius, showMap, hasCoordinates]);

  const searchAddress = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsSearching(true);
    try {
      const params = new URLSearchParams({
        q: query,
        format: "json",
        addressdetails: "1",
        limit: "5",
        countrycodes: "lt",
        "accept-language": "lt",
      });
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?${params}`,
        { headers: { "User-Agent": "Boardo/1.0" } },
      );
      if (resp.ok) {
        const data: NominatimResult[] = await resp.json();
        setSuggestions(data);
        setShowDropdown(data.length > 0);
      }
    } catch {
      // Silently fail
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    setFullAddress("");
    setStreetAddress("");

    if (searchTimer.current) clearTimeout(searchTimer.current);

    searchTimer.current = setTimeout(() => {
      searchAddress(val);
    }, 400);

    onChange({ city: val, address: val, latitude: null, longitude: null });
  };

  const handleSelectSuggestion = (result: NominatimResult) => {
    const addr = result.address;
    const city = extractCity(addr);
    const street = extractStreet(addr);
    const shortDisplay =
      city || result.display_name.split(",").slice(0, 2).join(",").trim();

    setInputValue(shortDisplay);
    setFullAddress(result.display_name);
    setStreetAddress(street);
    setSuggestions([]);
    setShowDropdown(false);

    onChange({
      city,
      address: result.display_name,
      latitude: parseFloat(result.lat),
      longitude: parseFloat(result.lon),
    });
  };

  const handleUseGeolocation = () => {
    if (!navigator.geolocation) return;

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        const result = await reverseGeocode(lat, lng);

        if (result) {
          const display =
            result.city ||
            result.address.split(",").slice(0, 2).join(",").trim();
          setInputValue(display);
          setFullAddress(result.address);
          setStreetAddress(result.street);
          onChange({
            city: result.city,
            address: result.address,
            latitude: lat,
            longitude: lng,
          });
        } else {
          onChange({ city: "", address: "", latitude: lat, longitude: lng });
        }
        setIsLocating(false);
      },
      () => {
        setIsLocating(false);
      },
      { enableHighAccuracy: false, timeout: 10000 },
    );
  };

  return (
    <div className={className}>
      {label && (
        <label className="mb-1 block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      <div ref={dropdownRef} className="relative">
        <div className="relative">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => {
              if (suggestions.length > 0) setShowDropdown(true);
            }}
            placeholder={placeholder}
            className="input pr-20"
          />
          <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1">
            {isSearching && (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-primary-800" />
            )}
            {hasCoordinates && (
              <span className="text-green-600" title="Vieta nustatyta">
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </span>
            )}
            <button
              type="button"
              onClick={handleUseGeolocation}
              disabled={isLocating}
              className="rounded p-1 text-gray-400 transition-colors hover:text-primary-800 disabled:opacity-50"
              title="Nustatyti pagal GPS"
            >
              {isLocating ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-primary-800" />
              ) : (
                <svg
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Autocomplete dropdown */}
        {showDropdown && suggestions.length > 0 && (
          <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
            {suggestions.map((result, i) => {
              const addr = result.address;
              const city = extractCity(addr);
              const street = extractStreet(addr);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleSelectSuggestion(result)}
                  className="flex w-full items-start gap-2 px-3 py-2 text-left transition-colors hover:bg-gray-50"
                >
                  <svg
                    className="mt-0.5 h-4 w-4 shrink-0 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {city || result.display_name.split(",")[0]}
                      {street ? `, ${street}` : ""}
                    </p>
                    <p className="truncate text-xs text-gray-500">
                      {result.display_name}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Street address display */}
      {hasCoordinates && (fullAddress || streetAddress) && (
        <div className="mt-1.5 flex items-start gap-1.5 text-xs text-gray-500">
          <svg
            className="mt-0.5 h-3 w-3 shrink-0 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
          </svg>
          <span className="min-w-0 break-words">{fullAddress}</span>
        </div>
      )}

      {/* Leaflet map */}
      {showMap && hasCoordinates && (
        <div className="mt-3 overflow-hidden rounded-lg border border-gray-200">
          <div
            ref={mapContainerRef}
            className="h-64 w-full"
            style={{ zIndex: 0 }}
          />
          <p className="bg-gray-50 px-3 py-1.5 text-[11px] text-gray-400">
            Vilkite žymeklį norėdami pakeisti vietą
          </p>
        </div>
      )}
    </div>
  );
}
